import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function UtilsDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Utility Functions & Helpers" description="A collection of utility functions, shortcuts, and helpers for data manipulation, caching,
          and common operations in financial data analysis."  canonicalUrl="https://axionquant.com/docs/client-libraries/python-SDK/utilities-and-helpers" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Utility Functions & Helpers</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          A collection of utility functions, shortcuts, and helpers for data manipulation, caching,
          and common operations in financial data analysis.
        </p>
        <p className="text-sm text-gray-600 mt-2">
          Import as: <code className="bg-gray-100 px-2 py-1 rounded">from axion import utils as axion_utils</code>
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Time Constants */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Time Constants</h2>
              <p className="text-gray-700 mb-6">
                Predefined string constants for natural language date references.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Date Constants</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Constant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Value</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">today</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'today'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current day reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">now</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'today'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Current time reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">tomorrow</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 day from now'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Next day reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">yesterday</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 day ago'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Previous day reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">weekago</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 week ago'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One week ago reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">weekfrom</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 week from today'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One week from now reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">monthago</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 month ago'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One month ago reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">monthfrom</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 month from now'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One month from now reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">yearago</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 year ago'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One year ago reference</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">yearfrom</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'1 year from now'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">One year from now reference</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Frequency Constants</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Constant</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Value</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">d, day</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'D'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Daily frequency</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">w, week</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'W'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Weekly frequency</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">m, month</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'M'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Monthly frequency</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">y, year</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'Y'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Yearly frequency</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">h, hour</td>
                      <td className="px-4 py-3 text-sm text-gray-700">'H'</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Hourly frequency</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-md font-medium text-blue-900 mb-2">Boolean Constants</h4>
              <div className="flex gap-4">
                <div className="text-sm">
                  <code className="text-blue-900 bg-blue-100 px-2 py-1 rounded">true = True</code>
                  <span className="text-blue-800 ml-2">Python boolean True</span>
                </div>
                <div className="text-sm">
                  <code className="text-blue-900 bg-blue-100 px-2 py-1 rounded">false = False</code>
                  <span className="text-blue-800 ml-2">Python boolean False</span>
                </div>
              </div>
            </div>
          </div>

          
        </div>

        {/* Caching Functions */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Caching Functions</h2>
              <p className="text-gray-700 mb-6">
                Functions for persistent caching of data to disk to avoid repeated computation.
              </p>
            </div>

            {/* cache */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">cache</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  cache(id, fn)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Caches the result of a function call. Returns cached result if it exists, otherwise calls the function and caches the result.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">id</code> (str) - Unique identifier for the cache entry</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">fn</code> (callable) - Function to call if cache doesn't exist</li>
                </ul>
              </div>
            </div>

            {/* save */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">save</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  save(id, obj)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Manually saves an object to the cache directory.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">id</code> (str) - Unique identifier for the cache entry</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">obj</code> (any) - Object to save to cache</li>
                </ul>
              </div>
            </div>

            {/* read */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">read</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  read(id)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Reads an object from the cache directory.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">id</code> (str) - Unique identifier for the cache entry</li>
                </ul>
              </div>
            </div>

            {/* scribe */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">scribe</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  scribe(df, id, cb)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Combines caching with concurrent processing using the <code>work</code> function.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">df</code> (DataFrame) - DataFrame to process</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">id</code> (str) - Cache identifier</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">cb</code> (callable) - Callback function for processing rows</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Cache Example" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Cache Example</p>
                <Code lang="python">{`def fetch_expensive_data():
    return pd.DataFrame({'AAPL': [150, 151, 152]})

# First call - executes and caches
data = axion_utils.cache('stock_prices', fetch_expensive_data)

# Second call - loads from cache
cached_data = axion_utils.cache('stock_prices', fetch_expensive_data)

# Cache directory: ./.axion_cache/`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Save & Read Example</p>
                <Code lang="python">{`# Manually save data
axion_utils.save('my_portfolio', {'AAPL': 150.25})

# Read it back
saved_data = axion_utils.read('my_portfolio')`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Scribe Example</p>
                <Code lang="python">{`tickers_df = axion_utils.df([
    {'ticker': 'AAPL'}, {'ticker': 'GOOG'}
])

def fetch_data(row):
    return row['ticker'], {'price': 150.25}

results = axion_utils.scribe(tickers_df, 'stock_data', fetch_data)`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Date Functions */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Date Functions</h2>
              <p className="text-gray-700 mb-6">
                Functions for parsing, converting, and manipulating dates.
              </p>
            </div>

            {/* d (function) */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Natural Language Date Parser</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  d(date_string)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Converts natural language date strings to YYYY-MM-DD format using parsedatetime library.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">date_string</code> (str) - Natural language date</li>
                </ul>
                <h4 className="text-md font-medium text-gray-900 mt-3 mb-2">Returns</h4>
                <p className="text-sm text-gray-700">String in YYYY-MM-DD format or None if parsing fails</p>
              </div>
            </div>

            {/* to_timestamp */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Date to Timestamp</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  to_timestamp(date)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Converts a date string in YYYY-MM-DD format to Unix timestamp.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">date</code> (str) - Date in YYYY-MM-DD format</li>
                </ul>
              </div>
            </div>

            {/* nearest_day */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nearest Trading Day</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  nearest_day(date_str, force=False)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Converts a date to the nearest trading day (Monday-Friday).
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">date_str</code> (str) - Date in YYYY-MM-DD format</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">force</code> (bool) - If True, returns same date even if weekend</li>
                </ul>
                <h4 className="text-md font-medium text-gray-900 mt-3 mb-2">Weekend Handling</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Saturday → Previous Friday</li>
                  <li>Sunday → Next Monday</li>
                  <li>Weekdays → Same day</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Date Parsing Examples" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Date Parsing Examples</p>
                <Code lang="python">{`today = axion_utils.d("today")
three_days_ago = axion_utils.d("3 days ago")
next_monday = axion_utils.d("next monday")

# With constants
last_month = axion_utils.d(axion_utils.monthago)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Timestamp & Trading Day</p>
                <Code lang="python">{`# Convert to timestamp
timestamp = axion_utils.to_timestamp("2024-01-15")

# Get nearest trading day
friday = axion_utils.nearest_day("2024-01-13")  # Saturday -> Friday
monday = axion_utils.nearest_day("2024-01-14")  # Sunday -> Monday`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* DataFrame Creation */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">DataFrame Creation</h2>
              <p className="text-gray-700 mb-6">
                Shortcut functions for creating and manipulating DataFrames.
              </p>
            </div>

            {/* df */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">DataFrame Shortcut</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  df(items)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Creates a pandas DataFrame from a list of dictionaries or other compatible data structures.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">items</code> (list) - List of dictionaries or data to convert to DataFrame</li>
                </ul>
              </div>
            </div>

            {/* pds */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">DataFrame List Converter</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  pds(l)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Converts a 2D list of dictionaries into a 1D list of DataFrames.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">l</code> (list) - 2D list of dictionaries</li>
                </ul>
              </div>
            </div>

            {/* simmer */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">List Flattener</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  simmer(arr)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Flattens a 2D list into a 1D list.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">arr</code> (list) - 2D list to flatten</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Create DataFrame" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Create DataFrame</p>
                <Code lang="python">{`stock_data = axion_utils.df([
    {'ticker': 'AAPL', 'price': 150.25},
    {'ticker': 'GOOG', 'price': 2815.50}
])`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Convert Nested Data</p>
                <Code lang="python">{`api_data = [
    [{'metric': 'revenue', 'value': 100000}],
    [{'metric': 'revenue', 'value': 200000}]
]
dataframes = axion_utils.pds(api_data)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Flatten Lists</p>
                <Code lang="python">{`nested = [[1, 2, 3], [4, 5], [6, 7, 8]]
flattened = axion_utils.simmer(nested)
# [1, 2, 3, 4, 5, 6, 7, 8]`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* DataFrame Operations */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">DataFrame Operations</h2>
              <p className="text-gray-700 mb-6">
                Functions for filtering, merging, and transforming DataFrames.
              </p>
            </div>

            {/* resample */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Date Range Filter</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  resample(df, dates, col='time')
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Filters DataFrame rows based on a date range string.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">df</code> (DataFrame) - DataFrame to filter</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dates</code> (str) - String like "1 week ago 1 month ago"</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column name containing dates</li>
                </ul>
              </div>
            </div>

            {/* filter */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Column Value Filter</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  filter(df, col, items)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Filters DataFrame to include only rows where column values are in the specified list.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">df</code> (DataFrame) - DataFrame to filter</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column name to filter on</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">items</code> (list) - List of values to include</li>
                </ul>
              </div>
            </div>

            {/* relativity */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Percentage Change Calculator</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  relativity(df, cols)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Calculates percentage change for specified columns and adds them as new columns.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">df</code> (DataFrame) - DataFrame with time series data</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">cols</code> (list) - List of column names to calculate percentage change for</li>
                </ul>
              </div>
            </div>

            {/* convert_text_to_zero */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Text to Zero Converter</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  convert_text_to_zero(value)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Converts text values, None, or NaN to zero, otherwise returns the numeric value.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">value</code> (any) - Value to convert</li>
                </ul>
              </div>
            </div>

            {/* dedup */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">List Deduplicator</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  dedup(lst)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Removes duplicate values from a list while preserving order.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">lst</code> (list) - List with possible duplicates</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Filter by Date" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Filter by Date</p>
                <Code lang="python">{`filtered = axion_utils.resample(
    data, 
    f"{axion_utils.weekago} {axion_utils.today}"
)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Filter by Values</p>
                <Code lang="python">{`tech_stocks = axion_utils.filter(
    stocks, 
    'sector', 
    ['Tech']
)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Calculate Returns</p>
                <Code lang="python">{`with_returns = axion_utils.relativity(
    prices, 
    ['price', 'volume']
)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Clean & Deduplicate</p>
                <Code lang="python">{`# Convert text to zero
cleaned = axion_utils.convert_text_to_zero('N/A')

# Remove duplicates
unique = axion_utils.dedup(['AAPL', 'GOOG', 'AAPL'])`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* DataFrame Combination */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">DataFrame Combination</h2>
              <p className="text-gray-700 mb-6">
                Functions for combining and merging multiple DataFrames in different ways.
              </p>
            </div>

            {/* stack */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Vertical Concatenation</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  stack(dfs)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Vertically concatenates multiple DataFrames with the same structure.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames to concatenate</li>
                </ul>
              </div>
            </div>

            {/* stitch */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Horizontal Merge</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  stitch(dfs, col='time')
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Merges multiple DataFrames horizontally on a common column (inner join).
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames to merge</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column name to join on</li>
                </ul>
              </div>
            </div>

            {/* snap */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Smart Merge with Renaming</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  snap(dfs, names=[], overwrite=[], col='time')
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Merges DataFrames with intelligent column renaming and suffix management.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames to merge</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">names</code> (list) - New names for columns</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">overwrite</code> (list) - Columns to rename</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Merge column</li>
                </ul>
              </div>
            </div>

            {/* indexed */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Price Index Averager</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  indexed(prices)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Averages multiple price DataFrames (OHLCV) by timestamp to create a composite index.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">prices</code> (dict) - Dictionary of ticker: DataFrame with OHLCV columns</li>
                </ul>
              </div>
            </div>

            {/* composite */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Fact Averager</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  composite(dfs, joins=['fact', 'label'], col='value')
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Combines and averages values from multiple fact/financial DataFrames.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames with financial facts</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">joins</code> (list) - Columns to group by</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column containing values to average</li>
                </ul>
              </div>
            </div>

            {/* contrast */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Fact Reshaper</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  contrast(dfs, joins, col="fact")
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Reshapes multiple fact DataFrames into a single indexed DataFrame for graphing.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames with financial facts</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">joins</code> (list) - Fact names to extract</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column containing fact names</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Vertical Stack" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Vertical Stack</p>
                <Code lang="python">{`all_data = axion_utils.stack([q1_data, q2_data])`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Horizontal Merge</p>
                <Code lang="python">{`combined = axion_utils.stitch([prices, economic])`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Smart Merge</p>
                <Code lang="python">{`merged = axion_utils.snap(
    [company_a, company_b],
    names=['A', 'B'],
    overwrite=['revenue']
)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Create Index</p>
                <Code lang="python">{`index = axion_utils.indexed({
    'AAPL': aapl_df,
    'GOOG': goog_df
})`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Average Estimates</p>
                <Code lang="python">{`consensus = axion_utils.composite([est1, est2])`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Functions */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Comparison Functions</h2>
              <p className="text-gray-700 mb-6">
                Functions for comparing and analyzing differences between DataFrames.
              </p>
            </div>

            {/* compare */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Multi-DataFrame Comparer</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  compare(dfs, joins=['fact','value'])
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Compares values across multiple DataFrames and calculates percentage differences.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames to compare</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">joins</code> (list) - Columns to join and compare on</li>
                </ul>
              </div>
            </div>

            {/* difference */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Set Difference Finder</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  difference(dfs, col)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Finds values in the first DataFrame that are not present in any subsequent DataFrame.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column name to compare</li>
                </ul>
              </div>
            </div>

            {/* overlap */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Set Overlap Finder</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  overlap(dfs, col)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Finds values that are present in all DataFrames.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">dfs</code> (list) - List of DataFrames</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column name to compare</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Compare Estimates" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Compare Estimates</p>
                <Code lang="python">{`comparison = axion_utils.compare([estimates_q1, estimates_q2])`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Find Differences</p>
                <Code lang="python">{`added = axion_utils.difference([new, old], 'ticker')
removed = axion_utils.difference([old, new], 'ticker')`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Find Overlap</p>
                <Code lang="python">{`common = axion_utils.overlap([fund_a, fund_b, fund_c], 'ticker')`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Analysis */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Performance Analysis</h2>
              <p className="text-gray-700 mb-6">
                Functions for analyzing price performance across multiple assets.
              </p>
            </div>

            {/* gainers */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Top Gainers Finder</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  gainers(prices, frame, col='close', limit=500, relative=True, reverse=True)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Identifies assets with the highest price gains over a specified period.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">prices</code> (dict) - Dictionary of ticker: DataFrame</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">frame</code> (int) - Lookback period in rows</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column to analyze</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">limit</code> (int) - Maximum number of results</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">relative</code> (bool) - Use percentage change or absolute change</li>
                </ul>
              </div>
            </div>

            {/* losers */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Top Losers Finder</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  losers(prices, frame, col='close', relative=True, limit=500)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Wrapper function that calls <code>gainers</code> with <code>reverse=False</code> to find worst performers.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">prices</code> (dict) - Dictionary of ticker: DataFrame</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">frame</code> (int) - Lookback period in rows</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">col</code> (str) - Column to analyze</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">limit</code> (int) - Maximum number of results</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">relative</code> (bool) - Use percentage change or absolute change</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px] space-y-6">
              <SectionActions sectionTitle="Find Gainers" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Find Gainers</p>
                <Code lang="python">{`top_gainers = axion_utils.gainers(
    prices, 
    frame=2, 
    limit=3
)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Find Losers</p>
                <Code lang="python">{`top_losers = axion_utils.losers(
    prices, 
    frame=2, 
    limit=3
)`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Concurrency */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Concurrency Function</h2>
              <p className="text-gray-700 mb-6">
                Function for parallel processing of DataFrame rows.
              </p>
            </div>

            {/* work */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Parallel Data Processor</h3>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  work(df, cb, ref)
                </code>
              </div>
              <p className="text-gray-700 mb-4">
                Processes DataFrame rows concurrently using a thread pool executor with progress bar.
              </p>
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Parameters</h4>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">df</code> (DataFrame) - DataFrame to process</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">cb</code> (callable) - Callback function that processes each row</li>
                  <li><code className="text-gray-800 bg-gray-100 px-1 rounded">ref</code> (dict) - Dictionary to store results (modified in place)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Parallel Processing" contentRef={sectionRef} />
              <div>
                <p className="text-gray-400 text-xs mb-2">Parallel Processing</p>
                <Code lang="python">{`tickers = axion_utils.df([
    {'ticker': 'AAPL'},
    {'ticker': 'GOOG'},
    {'ticker': 'MSFT'},
])

def fetch_data(row):
    ticker = row['ticker']
    return ticker, {'price': 150.25}

results = {}
axion_utils.work(tickers, fetch_data, results)`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Dependencies */}
        <div className="flex flex-col max-w-[1400px]">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Required Dependencies</h2>
            <p className="text-gray-700 mb-6">
              These utility functions require the following Python packages:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Core Libraries</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">pandas</code> - Data manipulation</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">numpy</code> - Numerical operations</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">pickle</code> - Object serialization</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">os</code> - File system operations</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Additional Libraries</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">parsedatetime</code> - Natural language date parsing</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">datetime</code> - Date/time manipulation</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">concurrent.futures</code> - Thread pool executor</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">tqdm</code> - Progress bars</li>
              </ul>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
